<template>
  <div
    v-if="show && feature"
    class="ags-popup-overlay"
    @click.self="$emit('close')"
  >
    <div class="ags-popup-content">
      <!-- Header -->
      <div class="ags-popup-header">
        <div>
          <h3 class="ags-popup-title">{{ feature.properties.name || 'Geographic Unit' }}</h3>
          <p class="ags-popup-subtitle">{{ layerType.replace('_', ' ').toUpperCase() }}: {{ feature.properties.state_code || feature.properties.code || 'N/A' }}</p>
        </div>
        <button @click="$emit('close')" class="ags-popup-close" aria-label="Close">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
      
      <!-- Body -->
      <div class="ags-popup-body">
        <!-- Geographic Information -->
        <div class="ags-popup-section">
          <h4 class="ags-popup-section-title">Geographic Information</h4>
          <div class="ags-popup-data-row">
            <div class="ags-popup-label">State:</div>
            <div class="ags-popup-value">{{ getStateName() }} ({{ feature.properties.state_code || feature.properties.state || 'N/A' }})</div>
          </div>
          <div v-if="feature.properties.county_name || feature.properties.county_code" class="ags-popup-data-row">
            <div class="ags-popup-label">County:</div>
            <div class="ags-popup-value">{{ feature.properties.county_name || `County ${feature.properties.county_code || feature.properties.county}` }}</div>
          </div>
          <div class="ags-popup-data-row">
            <div class="ags-popup-label">Population:</div>
            <div class="ags-popup-value">{{ formatNumber(feature.properties.population || feature.properties.ags_population_value) || 'N/A' }}</div>
          </div>
          <div v-if="feature.properties.area_sq_mi" class="ags-popup-data-row">
            <div class="ags-popup-label">Area:</div>
            <div class="ags-popup-value">{{ formatNumber(feature.properties.area_sq_mi) }} sq mi</div>
          </div>
          <div v-if="hasDensityData" class="ags-popup-data-row">
            <div class="ags-popup-label">Population Density:</div>
            <div class="ags-popup-value">{{ calculateDensity() }} people/sq mi</div>
          </div>
        </div>
        
        <!-- AGS Data Analysis -->
        <div v-if="selectedVariables.length > 0 && hasAgsIntensityData" class="ags-popup-section">
          <h4 class="ags-popup-section-title">AGS Data Analysis</h4>
          
          <!-- Intensity Bar with Percentage -->
          <div class="ags-popup-data-row intensity-row">
            <div class="ags-popup-label">Data Intensity:</div>
            <div class="ags-popup-value">
              <div class="intensity-bar-container">
                <div class="intensity-bar">
                  <div 
                    class="intensity-fill" 
                    :style="{ 
                      width: displayedIntensityPercent + '%',
                      backgroundColor: getIntensityColor(feature.properties.ags_intensity_level)
                    }"
                  ></div>
                </div>
                <span class="intensity-text">{{ displayedIntensityPercent }}%</span>
              </div>
            </div>
          </div>
          
          <!-- Classification -->
          <div class="ags-popup-data-row">
            <div class="ags-popup-label">Classification:</div>
            <div class="ags-popup-value">
              <span class="ags-classification-badge" :class="getIntensityBadgeClass(feature.properties.ags_intensity_level)">
                {{ formatIntensityLevel(feature.properties.ags_intensity_level).toUpperCase() }}
              </span>
            </div>
          </div>
          
          <!-- Ranking (if comparison reference available) -->
          <div v-if="feature.properties.ags_comparison_reference" class="ags-popup-data-row">
            <div class="ags-popup-label">Ranking:</div>
            <div class="ags-popup-value">
              {{ getRankingText() }}
            </div>
          </div>
        </div>
        
        <!-- Population Filtered Warning -->
        <div v-if="selectedVariables.length > 0 && isPopulationFiltered" class="ags-popup-section ags-popup-warning">
          <div class="ags-popup-warning-icon">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
          </div>
          <div class="ags-popup-warning-content">
            <h4 class="ags-popup-warning-title">No AGS Data Available</h4>
            <p class="ags-popup-warning-text">
              {{ getThresholdWarningMessage() }}
            </p>
            <div v-if="feature.properties.ags_threshold_info" class="ags-popup-warning-details">
              <div class="ags-popup-data-row">
                <div class="ags-popup-label">{{ getThresholdLabel() }}:</div>
                <div class="ags-popup-value">{{ getThresholdValue() }}</div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Variable Values Table -->
        <div v-if="selectedVariables.length > 0" class="ags-popup-section">
          <h4 class="ags-popup-section-title">
            {{ getAgsDataCategoryName() }} ({{ new Date().getFullYear() }})
          </h4>
          
          <div class="ags-popup-table">
            <div class="ags-popup-table-header">
              <div class="ags-popup-table-cell">VARIABLE</div>
              <div class="ags-popup-table-cell text-right">VALUE</div>
            </div>
            
            <!-- Single variable -->
            <div v-if="selectedVariables.length === 1" class="ags-popup-table-row">
              <div class="ags-popup-table-cell">{{ selectedVariables[0].label }}</div>
              <div class="ags-popup-table-cell text-right font-semibold">
                {{ feature.properties.ags_value !== null && feature.properties.ags_value !== undefined
                   ? formatAgsVariableValue(feature.properties.ags_value, selectedVariables[0].format)
                   : 'N/A' }}
              </div>
            </div>
            
            <!-- Multiple variables -->
            <div v-else v-for="variable in selectedVariables" :key="variable.path" class="ags-popup-table-row">
              <div class="ags-popup-table-cell">{{ variable.label }}</div>
              <div class="ags-popup-table-cell text-right font-semibold">
                {{ getAgsVariableValue(variable) }}
              </div>
            </div>
          </div>
        </div>
        
        <!-- Debug Information (Collapsible) -->
        <div v-if="selectedVariables.length > 0" class="ags-popup-section">
          <button @click="showDebugInfo = !showDebugInfo" class="ags-debug-toggle">
            🔍 AGS DATA DEBUG INFORMATION
            <svg class="w-4 h-4 transition-transform" :class="{ 'rotate-180': showDebugInfo }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>
          
          <div v-if="showDebugInfo" class="ags-debug-content">
            <!-- Step 0: Density Threshold Filtering -->
            <div class="ags-debug-step">
              <h5 class="ags-debug-step-title">STEP 0: DENSITY THRESHOLD FILTERING</h5>
              <div class="ags-debug-item">
                <span class="ags-debug-label">📏 Filtering Type:</span>
                <span class="ags-debug-value">{{ feature.properties.ags_threshold_info ? feature.properties.ags_threshold_info.type : 'density' }} based</span>
              </div>
              <div class="ags-debug-item">
                <span class="ags-debug-label">Population:</span>
                <span class="ags-debug-value">{{ formatNumber(feature.properties.population || feature.properties.ags_population_value || (feature.properties.ags_threshold_info ? feature.properties.ags_threshold_info.population : null)) || 'N/A' }}</span>
              </div>
              <div class="ags-debug-item">
                <span class="ags-debug-label">Area:</span>
                <span class="ags-debug-value">{{ formatNumber(feature.properties.area_sq_mi || (feature.properties.ags_threshold_info ? feature.properties.ags_threshold_info.area_sq_mi : null)) || 'N/A' }} sq mi</span>
              </div>
              <div v-if="hasDensityData" class="ags-debug-item">
                <span class="ags-debug-label">📊 Density Formula:</span>
                <span class="ags-debug-value">Population ÷ Area = Density</span>
              </div>
              <div v-if="hasDensityData" class="ags-debug-item">
                <span class="ags-debug-label">Calculation:</span>
                <span class="ags-debug-value">{{ formatNumber(feature.properties.population || feature.properties.ags_population_value || (feature.properties.ags_threshold_info ? feature.properties.ags_threshold_info.population : null)) }} ÷ {{ formatNumber(feature.properties.area_sq_mi || (feature.properties.ags_threshold_info ? feature.properties.ags_threshold_info.area_sq_mi : null)) }} = {{ calculateDensity() }} people/sq mi</span>
              </div>
              <div class="ags-debug-item">
                <span class="ags-debug-label">⚙️ Configured Threshold:</span>
                <span class="ags-debug-value">{{ feature.properties.ags_threshold_info ? feature.properties.ags_threshold_info.threshold : 350 }} people/sq mi</span>
              </div>
              <div v-if="feature.properties.ags_threshold_info && feature.properties.ags_threshold_info.density !== null && feature.properties.ags_threshold_info.density !== undefined" class="ags-debug-item">
                <span class="ags-debug-label">Comparison:</span>
                <span class="ags-debug-value">{{ formatNumber(feature.properties.ags_threshold_info.density) }} {{ feature.properties.ags_threshold_info.density <= feature.properties.ags_threshold_info.threshold ? '≤' : '>' }} {{ feature.properties.ags_threshold_info.threshold }}</span>
              </div>
              <div v-if="isPopulationFiltered" class="ags-debug-item" style="background: #fee2e2; padding: 8px; border-radius: 4px; margin-top: 8px;">
                <span class="ags-debug-label">✗ Result:</span>
                <span class="ags-debug-value" style="font-weight: 700; color: #dc2626;">FILTERED - Density too low for meaningful AGS data</span>
              </div>
              <div v-else-if="feature.properties.ags_threshold_info" class="ags-debug-item" style="background: #d1fae5; padding: 8px; border-radius: 4px; margin-top: 8px;">
                <span class="ags-debug-label">✓ Result:</span>
                <span class="ags-debug-value" style="font-weight: 700; color: #059669;">PASSED - Density sufficient for AGS data display</span>
              </div>
            </div>
            
            <!-- Step 1: Selected Variables -->
            <div class="ags-debug-step">
              <h5 class="ags-debug-step-title">STEP 1: SELECTED VARIABLES & EXTRACTED VALUES</h5>
              <div class="ags-debug-item">
                <span class="ags-debug-label">Total Selected Variables:</span>
                <span class="ags-debug-value">{{ selectedVariables.length }}</span>
              </div>
              <div v-for="variable in selectedVariables" :key="variable.path" class="ags-debug-item">
                <span class="ags-debug-label">{{ variable.label }}:</span>
                <span class="ags-debug-value">{{ getAgsVariableRawValue(variable) }}</span>
              </div>
            </div>
            
            <!-- Step 2: AGS Data Range -->
            <div v-if="feature.properties.ags_comparison_reference" class="ags-debug-step">
              <h5 class="ags-debug-step-title">STEP 2: AGS DATA RANGE (BACKEND CALCULATED)</h5>
              <div class="ags-debug-item">
                <span class="ags-debug-label">Min AGS Index ({{ comparisonScope }}):</span>
                <span class="ags-debug-value">{{ formatNumber(feature.properties.ags_comparison_reference.min_value) }}</span>
              </div>
              <div class="ags-debug-item">
                <span class="ags-debug-label">Max AGS Index ({{ comparisonScope }}):</span>
                <span class="ags-debug-value">{{ formatNumber(feature.properties.ags_comparison_reference.max_value) }}</span>
              </div>
              <div v-if="feature.properties.ags_comparison_reference.mean_value" class="ags-debug-item">
                <span class="ags-debug-label">Mean AGS Index:</span>
                <span class="ags-debug-value">{{ formatNumber(feature.properties.ags_comparison_reference.mean_value) }}</span>
              </div>
              <div v-if="feature.properties.ags_comparison_reference.median_value" class="ags-debug-item">
                <span class="ags-debug-label">Median AGS Index:</span>
                <span class="ags-debug-value">{{ formatNumber(feature.properties.ags_comparison_reference.median_value) }}</span>
              </div>
              <div class="ags-debug-item">
                <span class="ags-debug-label">Total Entities:</span>
                <span class="ags-debug-value">{{ feature.properties.ags_comparison_reference.total_entities }}</span>
              </div>
            </div>
            
            <!-- Step 3: Normalization (Single Variable) -->
            <div v-if="isSingleVariableMode && feature.properties.ags_value !== null" class="ags-debug-step">
              <h5 class="ags-debug-step-title">STEP 3: AGS INTENSITY NORMALIZATION (SINGLE VARIABLE)</h5>
              <div class="ags-debug-item">
                <span class="ags-debug-label">📊 Normalization Formula:</span>
                <span class="ags-debug-value">((Value - Min) / (Max - Min)) × 100</span>
              </div>
              <div class="ags-debug-item">
                <span class="ags-debug-label">This Area's AGS Value:</span>
                <span class="ags-debug-value">{{ formatNumber(feature.properties.ags_value) }}</span>
              </div>
              <div class="ags-debug-item">
                <span class="ags-debug-label">Step a: Subtract Min:</span>
                <span class="ags-debug-value">{{ formatNumber(feature.properties.ags_value) }} - {{ formatNumber(feature.properties.ags_comparison_reference.min_value) }} = {{ formatNumber(feature.properties.ags_value - feature.properties.ags_comparison_reference.min_value) }}</span>
              </div>
              <div class="ags-debug-item">
                <span class="ags-debug-label">Step b: Calculate Range:</span>
                <span class="ags-debug-value">{{ formatNumber(feature.properties.ags_comparison_reference.max_value) }} - {{ formatNumber(feature.properties.ags_comparison_reference.min_value) }} = {{ formatNumber(feature.properties.ags_comparison_reference.max_value - feature.properties.ags_comparison_reference.min_value) }}</span>
              </div>
              <div class="ags-debug-item">
                <span class="ags-debug-label">Step c: Divide and Multiply:</span>
                <span class="ags-debug-value">({{ formatNumber(feature.properties.ags_value - feature.properties.ags_comparison_reference.min_value) }} / {{ formatNumber(feature.properties.ags_comparison_reference.max_value - feature.properties.ags_comparison_reference.min_value) }}) × 100 = {{ displayedIntensityPercent }}%</span>
              </div>
              <div class="ags-debug-item" style="background: #fef3c7; padding: 8px; border-radius: 4px; margin-top: 8px;">
                <span class="ags-debug-label">✓ Normalized Score:</span>
                <span class="ags-debug-value" style="font-weight: 700;">{{ displayedIntensityPercent }}%</span>
              </div>
            </div>
            
            <!-- Step 3: Multi-Variable Aggregation -->
            <div v-if="isMultiVariableMode" class="ags-debug-step">
              <h5 class="ags-debug-step-title">STEP 3: MULTI-VARIABLE AGGREGATION & NORMALIZATION</h5>
              <div class="ags-debug-item">
                <span class="ags-debug-label">📊 Process:</span>
                <span class="ags-debug-value">Backend aggregates {{ selectedVariables.length }} variables → Normalizes to percentage</span>
              </div>
              <div v-if="feature.properties.ags_percentage !== null && feature.properties.ags_percentage !== undefined" class="ags-debug-item" style="background: #fef3c7; padding: 8px; border-radius: 4px; margin-top: 8px;">
                <span class="ags-debug-label">✓ Final Intensity Percentage:</span>
                <span class="ags-debug-value" style="font-weight: 700;">{{ displayedIntensityPercent }}%</span>
              </div>
            </div>
            
            <!-- Step 4: Intensity Level Classification -->
            <div class="ags-debug-step">
              <h5 class="ags-debug-step-title">STEP 4: MAP TO INTENSITY LEVEL & COLOR</h5>
              <div class="ags-debug-item">
                <span class="ags-debug-label">Intensity Thresholds:</span>
                <span class="ags-debug-value">0-14% VeryLow, 14-28% Low, 28-42% LowMod, 42-57% Mod, 57-71% ModHigh, 71-85% High, 85-100% VeryHigh</span>
              </div>
              <div class="ags-debug-item">
                <span class="ags-debug-label">Calculated Percentage:</span>
                <span class="ags-debug-value">{{ displayedIntensityPercent }}%</span>
              </div>
              <div class="ags-debug-item" style="background: #fef3c7; padding: 8px; border-radius: 4px; margin-top: 8px;">
                <span class="ags-debug-label">✓ Intensity Level:</span>
                <span class="ags-debug-value" style="font-weight: 700;">{{ formatIntensityLevel(feature.properties.ags_intensity_level) }}</span>
              </div>
              <div class="ags-debug-item" style="background: #fef3c7; padding: 8px; border-radius: 4px; margin-top: 8px;">
                <span class="ags-debug-label">✓ Color Assignment:</span>
                <span class="ags-debug-value" :style="{ backgroundColor: getIntensityColor(feature.properties.ags_intensity_level), color: 'white', padding: '4px 8px', borderRadius: '4px', fontWeight: 700 }">
                  {{ getIntensityColor(feature.properties.ags_intensity_level) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AgsPopup',
  
  props: {
    show: {
      type: Boolean,
      default: false
    },
    feature: {
      type: Object,
      default: null
    },
    selectedVariables: {
      type: Array,
      default: () => []
    },
    comparisonScope: {
      type: String,
      default: 'state'
    },
    layerType: {
      type: String,
      default: 'states'
    },
    reverseColors: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      showDebugInfo: false
    };
  },
  
  mounted() {
    // Debug logging
    console.log('=== AGS POPUP MOUNTED ===');
    console.log('Selected variables:', this.selectedVariables);
    console.log('Feature properties:', this.feature?.properties);
    console.log('AGS data:', this.feature?.properties?.ags_data);
    console.log('AGS intensity level:', this.feature?.properties?.ags_intensity_level);
    console.log('AGS value:', this.feature?.properties?.ags_value);
    console.log('AGS percentage:', this.feature?.properties?.ags_percentage);
    console.log('=== END AGS POPUP MOUNTED ===');
  },
  
  computed: {
    isSingleVariableMode() {
      return this.selectedVariables.length === 1;
    },
    
    isMultiVariableMode() {
      return this.selectedVariables.length > 1;
    },
    
    isPopulationFiltered() {
      // Check if this feature was filtered due to low population
      return this.feature.properties.ags_population_filtered === true;
    },
    
    hasDensityData() {
      const pop = this.feature.properties.population || this.feature.properties.ags_population_value;
      const area = this.feature.properties.area_sq_mi;
      return pop && area && area > 0;
    },
    
    hasAgsIntensityData() {
      // Don't show intensity data if population filtered
      if (this.isPopulationFiltered) {
        return false;
      }
      
      // Check if we have intensity level and it's not 'no_data'
      const hasIntensity = this.feature.properties.ags_intensity_level && 
                          this.feature.properties.ags_intensity_level !== 'no_data';
      
      // Or check if we have ags_data with actual values
      const hasAgsData = this.feature.properties.ags_data && 
                        Object.keys(this.feature.properties.ags_data).length > 0;
      
      console.log('hasAgsIntensityData check:', {
        hasIntensity,
        hasAgsData,
        intensity_level: this.feature.properties.ags_intensity_level,
        ags_data_keys: this.feature.properties.ags_data ? Object.keys(this.feature.properties.ags_data) : []
      });
      
      return hasIntensity || hasAgsData;
    },
    
    displayedIntensityPercent() {
      // Use backend-provided percentage if available
      if (this.feature.properties.ags_percentage !== null && this.feature.properties.ags_percentage !== undefined) {
        return Math.round(this.feature.properties.ags_percentage);
      }
      
      // Calculate for single variable mode
      if (this.isSingleVariableMode && this.feature.properties.ags_value !== null && this.feature.properties.ags_comparison_reference) {
        const min = this.feature.properties.ags_comparison_reference.min_value || 0;
        const max = this.feature.properties.ags_comparison_reference.max_value || 1;
        
        if (max === min) return 50;
        
        const percentage = ((this.feature.properties.ags_value - min) / (max - min)) * 100;
        return Math.round(Math.max(0, Math.min(100, percentage)));
      }
      
      // Fallback to intensity level mapping
      const intensityToPercent = {
        'very_low': 7,
        'low': 21,
        'low_moderate': 35,
        'moderate': 49,
        'moderate_high': 64,
        'high': 78,
        'very_high': 92,
        'no_data': 0
      };
      return intensityToPercent[this.feature.properties.ags_intensity_level] || 0;
    }
  },
  
  methods: {
    getStateName() {
      const stateCode = this.feature.properties.state_code || this.feature.properties.state;
      if (!stateCode) return 'N/A';
      
      const stateNames = {
        '01': 'Alabama', '02': 'Alaska', '04': 'Arizona', '05': 'Arkansas', '06': 'California',
        '08': 'Colorado', '09': 'Connecticut', '10': 'Delaware', '11': 'District of Columbia',
        '12': 'Florida', '13': 'Georgia', '15': 'Hawaii', '16': 'Idaho', '17': 'Illinois',
        '18': 'Indiana', '19': 'Iowa', '20': 'Kansas', '21': 'Kentucky', '22': 'Louisiana',
        '23': 'Maine', '24': 'Maryland', '25': 'Massachusetts', '26': 'Michigan', '27': 'Minnesota',
        '28': 'Mississippi', '29': 'Missouri', '30': 'Montana', '31': 'Nebraska', '32': 'Nevada',
        '33': 'New Hampshire', '34': 'New Jersey', '35': 'New Mexico', '36': 'New York',
        '37': 'North Carolina', '38': 'North Dakota', '39': 'Ohio', '40': 'Oklahoma',
        '41': 'Oregon', '42': 'Pennsylvania', '44': 'Rhode Island', '45': 'South Carolina',
        '46': 'South Dakota', '47': 'Tennessee', '48': 'Texas', '49': 'Utah', '50': 'Vermont',
        '51': 'Virginia', '53': 'Washington', '54': 'West Virginia', '55': 'Wisconsin', '56': 'Wyoming'
      };
      return stateNames[stateCode] || stateCode;
    },
    
    getIntensityColor(level) {
      const colors = {
        'very_low': '#059669',
        'low': '#22c55e',
        'low_moderate': '#84cc16',
        'moderate': '#eab308',
        'moderate_high': '#f97316',
        'high': '#ef4444',
        'very_high': '#dc2626',
        'no_data': '#9ca3af'
      };
      return colors[level] || '#9ca3af';
    },
    getIntensityBadgeClass(level) {
      if (!level) return 'intensity-no_data';
      
      // If colors are reversed, map intensity levels to opposite colors
      if (this.reverseColors) {
        const reversedMapping = {
          'very_low': 'intensity-very_high',
          'low': 'intensity-high',
          'low_moderate': 'intensity-moderate_high',
          'moderate': 'intensity-moderate',
          'moderate_high': 'intensity-low_moderate',
          'high': 'intensity-low',
          'very_high': 'intensity-very_low',
          'no_data': 'intensity-no_data'
        };
        return reversedMapping[level] || 'intensity-' + level;
      }
      
      return 'intensity-' + level;
    },
    
    formatIntensityLevel(level) {
      if (!level) return 'N/A';
      
      const levels = {
        'very_low': 'Very Low',
        'low': 'Low',
        'low_moderate': 'Low-Moderate',
        'moderate': 'Moderate',
        'moderate_high': 'Moderate-High',
        'high': 'High',
        'very_high': 'Very High',
        'no_data': 'No Data'
      };
      
      return levels[level] || level;
    },
    
    getRankingText() {
      const ref = this.feature.properties.ags_comparison_reference;
      if (!ref) return 'N/A';
      
      const value = this.feature.properties.ags_value;
      const range = ref.max_value - ref.min_value;
      const percentile = ((value - ref.min_value) / range * 100).toFixed(1);
      
      return `${percentile}th percentile of ${ref.total_entities} ${this.comparisonScope}s`;
    },
    
    getAgsDataCategoryName() {
      const agsData = this.feature.properties.ags_data;
      if (!agsData) return 'AGS Data';
      
      // Use category_name from first selected variable to access the data
      if (this.selectedVariables.length > 0) {
        const categoryName = this.selectedVariables[0].category_name;
        const categoryData = agsData[categoryName];
        
        if (categoryData && categoryData.category_display_name) {
          return categoryData.category_display_name;
        }
        
        // Fallback to category_name itself
        return categoryName;
      }
      
      return 'AGS Data';
    },
    
    getAgsVariableValue(variable) {
      const agsData = this.feature.properties.ags_data;
      if (!agsData) return 'N/A';
      
      // Access data using category_name as key: agsData[category_name].extracted_values[variable_path]
      const categoryData = agsData[variable.category_name];
      
      if (categoryData && categoryData.extracted_values) {
        const value = categoryData.extracted_values[variable.path];
        if (value !== undefined && value !== null) {
          return this.formatAgsVariableValue(value, variable.format);
        }
      }
      
      // Fallback to flat structure (single variable case)
      const value = agsData[variable.path];
      if (value !== undefined && value !== null) {
        return this.formatAgsVariableValue(value, variable.format);
      }
      
      return 'N/A';
    },
    
    getAgsVariableRawValue(variable) {
      const agsData = this.feature.properties.ags_data;
      if (!agsData) return 'N/A';
      
      // Access data using category_name as key: agsData[category_name].extracted_values[variable_path]
      const categoryData = agsData[variable.category_name];
      
      if (categoryData && categoryData.extracted_values) {
        const value = categoryData.extracted_values[variable.path];
        if (value !== undefined && value !== null) {
          return value;
        }
      }
      
      // Fallback to flat structure (single variable case)
      const value = agsData[variable.path];
      if (value !== undefined && value !== null) {
        return value;
      }
      
      return 'N/A';
    },
    
    formatAgsVariableValue(value, format) {
      if (value === null || value === undefined) return 'N/A';
      
      const num = parseFloat(value);
      if (isNaN(num)) return 'N/A';
      
      if (format === 'currency') {
        if (num >= 1000000) return '$' + (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return '$' + (num / 1000).toFixed(1) + 'k';
        return '$' + num.toLocaleString();
      } else if (format === 'percent') {
        return (num * 100).toFixed(1) + '%';
      } else {
        return this.formatNumber(num);
      }
    },
    
    formatNumber(value) {
      if (value === null || value === undefined) return 'N/A';
      
      const num = parseFloat(value);
      if (isNaN(num)) return 'N/A';
      
      if (num >= 1000000000) return (num / 1000000000).toFixed(1) + 'B';
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
      if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
      
      return num.toLocaleString();
    },
    
    getThresholdWarningMessage() {
      const thresholdInfo = this.feature.properties.ags_threshold_info;
      if (!thresholdInfo) {
        return 'This geographic unit has insufficient population for meaningful AGS data analysis.';
      }
      
      if (thresholdInfo.type === 'density') {
        return 'This geographic unit has insufficient population density for meaningful AGS data analysis.';
      } else {
        return 'This geographic unit has insufficient population for meaningful AGS data analysis.';
      }
    },
    
    getThresholdLabel() {
      const thresholdInfo = this.feature.properties.ags_threshold_info;
      if (!thresholdInfo) return 'Minimum Required';
      
      if (thresholdInfo.type === 'density') {
        return 'Minimum Required Density';
      } else {
        return 'Minimum Required Population';
      }
    },
    
    getThresholdValue() {
      const thresholdInfo = this.feature.properties.ags_threshold_info;
      if (!thresholdInfo || !thresholdInfo.threshold) return 'N/A';
      
      if (thresholdInfo.type === 'density') {
        return thresholdInfo.threshold + ' people/sq mi';
      } else {
        return this.formatNumber(thresholdInfo.threshold);
      }
    },
    
    calculateDensity() {
      // Try to get density from threshold info first (most accurate)
      if (this.feature.properties.ags_threshold_info && this.feature.properties.ags_threshold_info.density !== null) {
        return this.formatNumber(this.feature.properties.ags_threshold_info.density);
      }
      
      // Calculate from available data
      const pop = this.feature.properties.population || this.feature.properties.ags_population_value;
      const area = this.feature.properties.area_sq_mi;
      
      if (!pop || !area || area === 0) return 'N/A';
      
      const density = pop / area;
      return this.formatNumber(density);
    }
  }
};
</script>
